import { UUID } from "crypto";
import api from "@/app/api";

export type UploadStatus =
  | "pending"
  | "uploading"
  | "processing"
  | "completed"
  | "error"
  | "cancelled";
export type UploadType = "product" | "bill" | "partner";

export interface UploadTask {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
  result?: any;
  type: UploadType;
  supplierId?: UUID; // Required for bill uploads
  partnerType?: number; // Required for partner uploads
  stockSelection?: string; // For bill uploads
}

class UploadService {
  private tasks: Map<string, UploadTask> = new Map();
  private listeners: Set<(tasks: UploadTask[]) => void> = new Set();
  private processingQueue: string[] = [];
  private isProcessing: boolean = false;

  constructor() {
    // Start processing pending tasks
    this.processNextTask();
  }

  private async processNextTask() {
    if (this.isProcessing) {
      setTimeout(() => this.processNextTask(), 1000);
      return;
    }

    if (this.processingQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const taskId = this.processingQueue.shift()!;
    const task = this.tasks.get(taskId);

    if (task) {
      await this.processTask(task);
    }

    this.isProcessing = false;
    this.processNextTask();
  }

  private async processTask(task: UploadTask) {
    try {
      // Update status to uploading
      this.updateTask(task.id, { status: "uploading", progress: 0 });

      // Start fake progress for visual feedback
      let fakeProgress = 0;
      const progressInterval = setInterval(() => {
        fakeProgress += Math.random() * 2 * (1 - fakeProgress / 100);
        this.updateTask(task.id, { progress: Math.min(fakeProgress, 99) });
      }, 100);

      // Process the file based on its type and upload type
      let result;
      if (task.type === "product") {
        try {
          const isTextFile = task.file.type === "text/plain";
          const textContent = isTextFile ? await task.file.text() : null;

          result = await api.products.file.postImport({
            text: textContent,
            file: isTextFile ? null : task.file,
          });

          clearInterval(progressInterval);
          this.updateTask(task.id, {
            status: "completed",
            progress: 100,
            result,
          });
        } catch (error) {
          clearInterval(progressInterval);
          throw error instanceof Error
            ? error
            : new Error("Unknown error during product import");
        }
      } else if (task.type === "bill") {
        if (!task.supplierId) {
          throw new Error("Supplier ID is required for bill uploads");
        }

        try {
          // Call the API endpoint with proper error handling
          result = await api.orders.aiImport({
            partnerId: task.supplierId,
            file_content: task.file,
            original_filename: task.file.name,
            stockSelection: task.stockSelection || "supplier",
          });

          // Clear the progress interval and set to 100% on success
          clearInterval(progressInterval);
          this.updateTask(task.id, {
            status: "completed",
            progress: 100,
            result,
          });
        } catch (error) {
          clearInterval(progressInterval);
          throw error instanceof Error
            ? error
            : new Error("Unknown error during import");
        }
      } else if (task.type === "partner") {
        if (!task.partnerType) {
          throw new Error("Partner type is required for partner uploads");
        }
        try {
          result = await api.partners.postFileImport({
            file_content: task.file,
            original_filename: task.file.name,
            partner_type: task.partnerType,
          });
          clearInterval(progressInterval);
          this.updateTask(task.id, {
            status: "completed",
            progress: 100,
            result,
          });
        } catch (error) {
          clearInterval(progressInterval);
          throw error instanceof Error
            ? error
            : new Error("Unknown error during partner import");
        }
      }
    } catch (error) {
      this.updateTask(task.id, {
        status: "error",
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  private updateTask(taskId: string, updates: Partial<UploadTask>) {
    const task = this.tasks.get(taskId);
    if (task) {
      this.tasks.set(taskId, { ...task, ...updates });
      this.notifyListeners();
    }
  }

  private notifyListeners() {
    const tasks = Array.from(this.tasks.values());
    this.listeners.forEach((listener) => listener(tasks));
  }

  public addTasks(
    files: File[],
    type: UploadType,
    options?: {
      supplierId?: UUID;
      partnerType?: number;
      stockSelection?: string;
    },
  ): string[] {
    const taskIds = files.map((file) => {
      const taskId = Math.random().toString(36).substring(7);
      const task: UploadTask = {
        id: taskId,
        file,
        status: "pending",
        progress: 0,
        type,
        supplierId: options?.supplierId,
        partnerType: options?.partnerType,
        stockSelection: options?.stockSelection,
      };
      this.tasks.set(taskId, task);
      return taskId;
    });

    // Add tasks to queue and start processing immediately
    this.processingQueue.push(...taskIds);
    this.notifyListeners();

    // Start processing if not already running
    if (!this.isProcessing) {
      this.processNextTask();
    }

    return taskIds;
  }

  public getTasks(): UploadTask[] {
    return Array.from(this.tasks.values());
  }

  public subscribe(listener: (tasks: UploadTask[]) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public removeTask(taskId: string) {
    this.tasks.delete(taskId);
    this.processingQueue = this.processingQueue.filter((id) => id !== taskId);
    this.notifyListeners();
  }
}

// Create a singleton instance
export const uploadService = new UploadService();
